// Copyright (c) StrangeBee 2024

import { FormSubmitButton } from '@Atoms/Buttons/FormSubmitButton/FormSubmitButton';
import UploadSingleFile from '@Atoms/UploadSingleFile/UploadSingleFile';
import { classList } from '@Helpers/className';
import { useLocalMessage } from '@Helpers/locale';
import { THButton } from '@Molecules/Inputs/THButton/THButton';
import THDrawer from '@Molecules/THDrawer/THDrawer';
import { THForm } from '@Molecules/THForm/THForm';
import { Space, Modal, Spin } from 'antd';
import { UploadFile } from 'antd/lib/upload/interface';
import React, { ReactElement, ReactNode, useEffect, useState } from 'react';

import './FileImportDrawer.scss';

interface ImportButtonProps {
    label: string;
    onClick: () => void;
}

interface FileImportDrawerProps {
    onSubmit: (formData: FormData) => Promise<void>;
    className?: string;
    triggerElem?: ReactElement;
    footerLabel?: string;
    header: ReactNode;
    drawerTitle?: string;
    uploadTitle?: string;
    uploadSubtitle?: string;
    accept?: string;
    name?: string;
    children?: ReactNode;
    preValidate?: boolean;
    visible?: boolean;
    onVisibleChange?: (visible: boolean) => void;
}

export const ImportButton = ({ label, onClick }: ImportButtonProps): ReactElement => (
    <THButton className="file-import__trigger-button" onClick={onClick}>
        {label}
    </THButton>
);

export const FileImportDrawer = ({
    onSubmit,
    header,
    className,
    drawerTitle = '',
    uploadTitle = '',
    uploadSubtitle = '',
    triggerElem,
    footerLabel = '',
    accept = '.zip',
    name = 'file',
    children,
    preValidate = true,
    visible,
    onVisibleChange,
}: FileImportDrawerProps): ReactElement => {
    const i = useLocalMessage();

    const [disabled, setDisabled] = useState(true);
    const [file, setFile] = useState<UploadFile<any>>();
    const [loading, setLoading] = useState(false);

    useEffect(() => {
        setDisabled(!(preValidate && file));
    }, [file, preValidate]);

    return (
        <>
            <THDrawer
                title={drawerTitle}
                className={classList('file-import__drawer', 'drawer-form', className)}
                triggerElem={triggerElem}
                width="35vw"
                afterVisibleChange={(visibility) => {
                    if (!visibility) {
                        setFile(undefined);
                    }
                    onVisibleChange?.(visibility);
                }}
                visible={visible}
            >
                {(toggleDrawer) => (
                    <THForm.Layout
                        footer={
                            <FormSubmitButton
                                disabled={disabled}
                                footerText={footerLabel}
                                onSubmit={() => {
                                    if (file) {
                                        const formData = new FormData();
                                        formData.set(name, file as any);
                                        toggleDrawer();
                                        setLoading?.(true);

                                        onSubmit(formData)
                                            .then(() => {
                                                setFile(undefined);
                                            })
                                            .finally(() => {
                                                setLoading?.(false);
                                            });
                                    }
                                }}
                            />
                        }
                    >
                        <Space direction="vertical" className="file-import__container" size="large">
                            {header}
                            <Space direction="vertical" className="file-import__upload">
                                <span className="file-import__upload-title">{uploadTitle}</span>
                                {children}
                                <UploadSingleFile
                                    text={i('taxonomy.import.upload.content')}
                                    value={file}
                                    onChange={(data): void => setFile(data)}
                                    accept={accept}
                                />
                                <span>{uploadSubtitle}</span>
                            </Space>
                        </Space>
                    </THForm.Layout>
                )}
            </THDrawer>

            <Modal visible={loading} closable={false} footer={null} className="file-import__modal">
                <Spin className="file-import__modal-spin" />
                <p className="file-import__modal-label">Loading...</p>
            </Modal>
        </>
    );
};
